import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Package, Plus, Minus, Trash2, Loader2, ShoppingCart } from "lucide-react";

const CART_KEY = "droneops_cart";
const imgUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
const SHIPPING = [
  { key: "standard", label: "Standard (3–5 days)", cost: 15 },
  { key: "express", label: "Express (1–2 days)", cost: 35 },
  { key: "pickup", label: "Pickup — no charge", cost: 0 },
];

export default function CartPage() {
  const navigate = useNavigate();
  const [parts, setParts] = useState([]);
  const [cart, setCart] = useState(() => { try { return JSON.parse(sessionStorage.getItem(CART_KEY)) || {}; } catch { return {}; } });
  const [loading, setLoading] = useState(true);
  const [ship, setShip] = useState("standard");
  const [checkingOut, setCheckingOut] = useState(false);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", billing_address: "", delivery_address: "" });

  useEffect(() => { api.get("/parts").then((r) => setParts(r.data)).finally(() => setLoading(false)); }, []);
  useEffect(() => { sessionStorage.setItem(CART_KEY, JSON.stringify(cart)); window.dispatchEvent(new Event("cart-updated")); }, [cart]);

  const map = useMemo(() => Object.fromEntries(parts.map((p) => [p.id, p])), [parts]);
  const lines = Object.entries(cart).map(([id, qty]) => ({ part: map[id], qty })).filter((l) => l.part);
  const subtotal = lines.reduce((s, l) => s + l.part.price * l.qty, 0);
  const shipCost = SHIPPING.find((s) => s.key === ship)?.cost || 0;
  const total = subtotal + shipCost;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setQty = (id, q) => setCart((c) => { const n = { ...c }; if (q <= 0) delete n[id]; else n[id] = q; return n; });

  const checkout = async () => {
    if (lines.length === 0) { toast.error("Your cart is empty"); return; }
    if (!form.customer_name.trim()) { toast.error("Please enter your name"); return; }
    if (ship !== "pickup" && !form.delivery_address.trim()) { toast.error("Please enter a delivery address"); return; }
    setCheckingOut(true);
    try {
      const r = await api.post("/checkout", {
        items: lines.map((l) => ({ part_id: l.part.id, quantity: l.qty })),
        origin_url: window.location.origin,
        shipping_amount: shipCost, shipping_method: ship, ...form,
      });
      window.location.href = r.data.url;
    } catch { toast.error("Checkout failed"); setCheckingOut(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>;

  return (
    <div className="space-y-6 fade-up" data-testid="cart-page">
      <button onClick={() => navigate("/parts")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="cart-back-btn"><ArrowLeft className="h-4 w-4" /> Continue shopping</button>
      <h1 className="text-3xl font-bold font-heading text-slate-900 flex items-center gap-2"><ShoppingCart className="h-7 w-7 text-lime-500" /> Your Cart</h1>

      {lines.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
          <Package className="h-10 w-10 text-slate-300 mx-auto" />
          <p className="mt-3 text-slate-500">Your cart is empty.</p>
          <Button onClick={() => navigate("/parts")} className="mt-4 bg-slate-900 text-white">Browse parts</Button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6 items-start">
          {/* Items + form */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="cart-items">
              {lines.map((l) => (
                <div key={l.part.id} className="flex items-center gap-4 p-4" data-testid={`cart-item-${l.part.id}`}>
                  <div className="h-14 w-14 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                    {l.part.image_path ? <img src={imgUrl(l.part.image_path)} alt="" className="h-full w-full object-cover" /> : <Package className="h-6 w-6 text-slate-300" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate">{l.part.name}</p>
                    <p className="text-sm text-slate-500">${l.part.price.toFixed(2)} each</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQty(l.part.id, l.qty - 1)} data-testid={`cart-dec-${l.part.id}`}><Minus className="h-3 w-3" /></Button>
                    <span className="w-8 text-center text-sm font-medium" data-testid={`cart-qty-${l.part.id}`}>{l.qty}</span>
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQty(l.part.id, l.qty + 1)} data-testid={`cart-inc-${l.part.id}`}><Plus className="h-3 w-3" /></Button>
                  </div>
                  <span className="w-20 text-right font-semibold font-heading text-slate-900 shrink-0">${(l.part.price * l.qty).toFixed(2)}</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-300 hover:text-red-600 shrink-0" onClick={() => setQty(l.part.id, 0)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
              <h2 className="font-heading font-semibold text-slate-900">Customer & delivery details</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Full name</Label><Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="mt-1.5" data-testid="cust-name" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label><Input type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className="mt-1.5" data-testid="cust-email" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</Label><Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className="mt-1.5" /></div>
              </div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Billing address</Label><Textarea value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} className="mt-1.5" rows={2} data-testid="cust-billing" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Delivery address</Label><Textarea value={form.delivery_address} onChange={(e) => set("delivery_address", e.target.value)} className="mt-1.5" rows={2} data-testid="cust-delivery" /></div>
            </div>
          </div>

          {/* Order summary */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:sticky lg:top-24 space-y-4" data-testid="order-summary">
            <h2 className="font-heading font-semibold text-slate-900">Order summary</h2>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Shipping</Label>
              <div className="mt-2 space-y-2">
                {SHIPPING.map((s) => (
                  <label key={s.key} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 cursor-pointer ${ship === s.key ? "border-lime-400 bg-lime-50" : "border-slate-200"}`} data-testid={`shipping-${s.key}`}>
                    <span className="flex items-center gap-2 text-sm text-slate-700"><input type="radio" name="ship" checked={ship === s.key} onChange={() => setShip(s.key)} className="accent-lime-500" /> {s.label}</span>
                    <span className="text-sm font-medium">${s.cost.toFixed(2)}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="border-t border-slate-100 pt-3 space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span data-testid="summary-subtotal">${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-slate-600"><span>Shipping</span><span data-testid="summary-shipping">${shipCost.toFixed(2)}</span></div>
              <div className="flex justify-between font-heading font-bold text-lg text-slate-900 pt-1"><span>Total</span><span data-testid="summary-total">${total.toFixed(2)}</span></div>
            </div>
            <Button onClick={checkout} disabled={checkingOut} className="w-full bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="checkout-btn">
              {checkingOut ? "Redirecting…" : "Proceed to checkout"}
            </Button>
            <p className="text-[11px] text-slate-400 text-center">Secure payment via Stripe.</p>
          </div>
        </div>
      )}
    </div>
  );
}
